/**
 * Getting Started Guide
 * Quick overview and first steps for new users
 */

import {
  Users,
  Shield,
  LayoutDashboard,
  Calendar,
  Car,
  UserCircle,
  Settings,
  BarChart3,
} from 'lucide-react';
import { pageStyles, cardStyles } from '../../lib/styles';

export function GettingStarted() {
  return (
    <div className={pageStyles.container}>
      {/* Overview */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <span className="text-xs font-medium text-gray-900">Que es Leal CRM?</span>
        </div>
        <div className={`${cardStyles.content} prose prose-gray max-w-none`}>
          <p>
            Leal CRM es una plataforma completa para la gestion de talleres de
            reparacion de vehiculos. Te permite gestionar citas, hacer
            seguimiento de trabajos, comunicarte con clientes y optimizar las
            operaciones de tu taller.
          </p>
          <h4>Funciones principales:</h4>
          <ul>
            <li>Gestion de citas online y presenciales</li>
            <li>Seguimiento del estado de reparaciones</li>
            <li>Base de datos de clientes y vehiculos</li>
            <li>Portal de seguimiento para clientes</li>
            <li>Widget de reservas para tu pagina web</li>
            <li>Configuracion flexible del taller</li>
          </ul>
        </div>
      </div>

      {/* User Roles */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <div className="flex items-center gap-1.5">
            <Users className="h-3.5 w-3.5 text-blue-500" />
            <span className="text-xs font-medium text-gray-900">Roles de Usuario</span>
          </div>
        </div>
        <div className={cardStyles.content}>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="p-4 rounded-lg bg-blue-50 border border-blue-100">
              <div className="flex items-center gap-2 mb-2">
                <Shield className="h-5 w-5 text-blue-600" />
                <h4 className="font-semibold text-blue-900">Administrador</h4>
              </div>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>Acceso completo a todas las funciones</li>
                <li>Gestion de usuarios y permisos</li>
                <li>Configuracion del sistema</li>
                <li>Informes y estadisticas</li>
              </ul>
            </div>
            <div className="p-4 rounded-lg bg-green-50 border border-green-100">
              <div className="flex items-center gap-2 mb-2">
                <UserCircle className="h-5 w-5 text-green-600" />
                <h4 className="font-semibold text-green-900">Staff</h4>
              </div>
              <ul className="text-sm text-green-800 space-y-1">
                <li>Crear y gestionar citas</li>
                <li>Actualizar estados de trabajos</li>
                <li>Ver informacion de clientes</li>
                <li>Acceso al tablero de trabajos</li>
              </ul>
            </div>
          </div>
        </div>
      </div>

      {/* Navigation Guide */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <span className="text-xs font-medium text-gray-900">Navegacion por la Plataforma</span>
        </div>
        <div className={cardStyles.content}>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded bg-gray-100">
                <LayoutDashboard className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Dashboard</h4>
                <p className="text-sm text-gray-600">
                  Vista general con estadisticas del dia, citas proximas y
                  accesos rapidos a las funciones principales.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="p-2 rounded bg-gray-100">
                <Calendar className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Citas</h4>
                <p className="text-sm text-gray-600">
                  Lista de todas las citas. Puedes filtrar por estado, buscar
                  por cliente o vehiculo, y crear nuevas citas.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="p-2 rounded bg-gray-100">
                <Car className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Vehiculos</h4>
                <p className="text-sm text-gray-600">
                  Base de datos de vehiculos con historial de servicio, datos
                  del propietario y edicion en linea.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="p-2 rounded bg-gray-100">
                <UserCircle className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Clientes</h4>
                <p className="text-sm text-gray-600">
                  Gestion de clientes con informacion de contacto, vehiculos
                  asociados e historial de citas.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="p-2 rounded bg-gray-100">
                <BarChart3 className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Tablero de Trabajos</h4>
                <p className="text-sm text-gray-600">
                  Vista Kanban de todos los trabajos en curso, organizado por
                  estado. Ideal para el equipo del taller.
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <div className="p-2 rounded bg-gray-100">
                <Settings className="h-5 w-5 text-gray-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Configuracion</h4>
                <p className="text-sm text-gray-600">
                  Ajustes de perfil, seguridad y preferencias personales.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* First Steps */}
      <div className={cardStyles.container}>
        <div className={cardStyles.header}>
          <span className="text-xs font-medium text-gray-900">Primeros Pasos Recomendados</span>
        </div>
        <div className={cardStyles.content}>
          <ol className="space-y-3">
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white text-sm flex items-center justify-center">
                1
              </span>
              <div>
                <h4 className="font-medium text-gray-900">
                  Configura tu perfil
                </h4>
                <p className="text-sm text-gray-600">
                  Ve a Ajustes y actualiza tu informacion personal y
                  preferencias.
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white text-sm flex items-center justify-center">
                2
              </span>
              <div>
                <h4 className="font-medium text-gray-900">
                  Explora el Dashboard
                </h4>
                <p className="text-sm text-gray-600">
                  Familiarizate con la vista general y las estadisticas del
                  dia.
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white text-sm flex items-center justify-center">
                3
              </span>
              <div>
                <h4 className="font-medium text-gray-900">
                  Crea tu primera cita
                </h4>
                <p className="text-sm text-gray-600">
                  Practica creando una cita de prueba para entender el flujo.
                </p>
              </div>
            </li>
            <li className="flex items-start gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-600 text-white text-sm flex items-center justify-center">
                4
              </span>
              <div>
                <h4 className="font-medium text-gray-900">
                  Revisa el Tablero de Trabajos
                </h4>
                <p className="text-sm text-gray-600">
                  Aprende a mover trabajos entre estados y actualizar su
                  progreso.
                </p>
              </div>
            </li>
          </ol>
        </div>
      </div>
    </div>
  );
}

export default GettingStarted;
